import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import InductivePowerChart from "../components/InductivePowerChart";
import ReactivePowerReportDialog from "../components/ReactivePowerReportDialog";

function breakLargeGaps(data, maxGapMs = 60 * 60 * 1000) {
    if (!data.length) return [];
    const result = [data[0]];
    for (let i = 1; i < data.length; i++) {
        const prev = data[i - 1];
        const curr = data[i];
        const gap = curr.time - prev.time;
        if (gap > maxGapMs) {
            result.push({
                time: prev.time + 1,
                QL1: null,
                QL2: null,
                QL3: null,
            });
            result.push({
                time: curr.time - 1,
                QL1: null,
                QL2: null,
                QL3: null,
            });
        }
        result.push(curr);
    }
    return result;
}

export default function InductivePowerPage({ deviceType = "analyzer" }) {
    const { deviceId } = useParams();

    const saved = JSON.parse(localStorage.getItem("selectedDevice") || "{}");
    const isVirtual = saved.mode === "virtual";
    const targetId = isVirtual ? saved.virtualId : deviceId;

    const [data, setData] = useState([]);
    const [range, setRange] = useState("1G");
    const [reportOpen, setReportOpen] = useState(false);

    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    // ... (rest of the code omitted for brevity but logic is same until return) ...
    const ENDPOINT_PREFIX = isVirtual
        ? "/virtual"
        : deviceType === "gruparge"
            ? "/gruparge"
            : deviceType === "reactive"
                ? "/reactive"
                : "/measurements";

    function getRangeMs(r) {
        switch (r) {
            case "1S": return 1 * 60 * 60 * 1000;
            case "3S": return 3 * 60 * 60 * 1000;
            case "6S": return 6 * 60 * 60 * 1000;
            case "12S": return 12 * 60 * 60 * 1000;
            case "1G": return 1 * 24 * 60 * 60 * 1000;
            case "3G": return 3 * 24 * 60 * 60 * 1000;
            case "1H": return 7 * 24 * 60 * 60 * 1000;
            default: return 1 * 24 * 60 * 60 * 1000;
        }
    }

    async function fetchData() {
        try {
            const end = new Date();
            const start = new Date(end.getTime() - getRangeMs(range));

            const url = `${API_BASE}${ENDPOINT_PREFIX}/inductive-power?deviceId=${targetId}&start=${start.toISOString()}&end=${end.toISOString()}`;
            const res = await fetch(url);
            const rows = await res.json();

            const formatted = rows.map((r) => ({
                time: new Date(r.time).getTime(),
                QL1: r.QL1,
                QL2: r.QL2,
                QL3: r.QL3,
            }));

            setData(breakLargeGaps(formatted));
        } catch (err) {
            console.error("fetchData error:", err);
        }
    }

    useEffect(() => {
        if (!targetId) return;
        fetchData();
        const i = setInterval(fetchData, 5000);
        return () => clearInterval(i);
    }, [API_BASE, targetId, range]);

    return (
        <div className="p-4 md:p-6 grid grid-cols-1 gap-6">
            <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 flex flex-col">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Endüktif Güç (VAr)</h2>
                <InductivePowerChart
                    data={data}
                    range={range}
                    onRangeChange={setRange}
                    deviceType={deviceType}
                />
            </div>

            <div className="flex justify-start">
                <button
                    onClick={() => setReportOpen(true)}
                    className="bg-sky-600 text-white px-3 py-2 rounded-md hover:bg-sky-700 text-sm"
                >
                    Raporla
                </button>
            </div>

            <ReactivePowerReportDialog
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                deviceId={targetId}
                deviceType={deviceType}
                API_BASE={API_BASE}
                isVirtual={isVirtual}
                powerType="inductive"
            />
        </div>
    );
}
